import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { PlanCode } from '../config/commercialPlans';
import type { SeatBandCode } from '../config/seatBands';

const GA_SCRIPT_ID = 'frontpage-ga4-script';
const SECTION_THRESHOLD = 0.4;

export const marketingEventNames = [
  'hero_cta_click',
  'trust_band_cta_click',
  'demo_form_start',
  'demo_form_submit_success',
  'demo_form_submit_error',
  'pricing_plan_select',
  'section_view',
] as const;

export type MarketingEventName = (typeof marketingEventNames)[number];

export type MarketingSectionId =
  | 'home_hero'
  | 'home_operation'
  | 'home_trust_band'
  | 'home_traceability'
  | 'home_compliance'
  | 'home_finance_flow'
  | 'home_ai_preview'
  | 'home_modules_preview'
  | 'home_pricing_preview'
  | 'home_contact'
  | 'pricing_trust_panel'
  | 'modules_finance_core'
  | 'ai_traceability';

export interface MarketingEventPayloadMap {
  hero_cta_click: {
    location:
      | 'home_hero'
      | 'home_mid_cta'
      | 'home_ai_proof'
      | 'pricing_intro'
      | 'pricing_hero'
      | 'site_header'
      | 'mobile_menu'
      | 'sticky_mobile_bar';
    cta:
      | 'request_demo'
      | 'view_plans'
      | 'view_ai'
      | 'view_how_it_works'
      | 'publish_free'
      | 'browse_marketplace'
      | 'free_company_register';
  };
  trust_band_cta_click: {
    location: 'home_trust_band' | 'pricing_trust_panel';
    cta: 'request_demo';
  };
  demo_form_start: {
    variant: 'full' | 'minimal';
    sourcePage: string;
  };
  demo_form_submit_success: {
    variant: 'full' | 'minimal';
    sourcePage: string;
    planInterest?: PlanCode;
    employeeRange?: SeatBandCode;
  };
  demo_form_submit_error: {
    variant: 'full' | 'minimal';
    sourcePage: string;
    errorType: 'validation' | 'network' | 'unknown';
  };
  pricing_plan_select: {
    planCode: PlanCode;
    seatBand: SeatBandCode;
    location:
      | 'pricing_plan_card'
      | 'pricing_hero'
      | 'home_free_plan_card'
      | 'home_pricing_plan_card';
  };
  section_view: {
    sectionId: MarketingSectionId;
  };
}

type GtagFunction = {
  (command: 'js', date: Date): void;
  (
    command: 'config',
    measurementId: string,
    params?: Record<string, unknown>
  ): void;
  (command: 'event', eventName: string, params?: Record<string, unknown>): void;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
  }
}

function getPagePath(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return window.location.pathname;
}

export function initializeAnalytics(measurementId?: string): void {
  if (typeof window === 'undefined' || !measurementId) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];

  if (!window.gtag) {
    const gtag: GtagFunction = ((
      command: 'js' | 'config' | 'event',
      target: Date | string,
      params?: Record<string, unknown>
    ) => {
      window.dataLayer?.push([command, target, params]);
    }) as GtagFunction;

    window.gtag = gtag;
  }

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    transport_type: 'beacon',
    page_path: getPagePath(),
  });
}

export function trackMarketingEvent<K extends MarketingEventName>(
  eventName: K,
  payload: MarketingEventPayloadMap[K]
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const enrichedPayload: Record<string, unknown> = {
    ...payload,
    source: 'inmueble_red_frontpage',
    page_path: getPagePath(),
    emitted_at: new Date().toISOString(),
  };

  if (window.gtag) {
    window.gtag('event', eventName, enrichedPayload);
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: eventName, ...enrichedPayload });
}

export function useSectionViewTracking(
  sectionId: MarketingSectionId
): RefObject<HTMLElement | null> {
  const sectionRef = useRef<HTMLElement | null>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const target = sectionRef.current;
    if (!target || hasTrackedRef.current) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      trackMarketingEvent('section_view', { sectionId });
      hasTrackedRef.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry || !entry.isIntersecting || hasTrackedRef.current) {
          return;
        }

        hasTrackedRef.current = true;
        trackMarketingEvent('section_view', { sectionId });
        observer.disconnect();
      },
      {
        threshold: SECTION_THRESHOLD,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [sectionId]);

  return sectionRef;
}
